import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useForm } from 'react-hook-form';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { Shield, Loader2 } from 'lucide-react';
import { usePayoutMethods } from '@/hooks/usePayoutMethods';
import { LinkedAccountsList } from './LinkedAccountsList';

interface LinkAccountsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const LinkAccountsModal = ({ open, onOpenChange }: LinkAccountsModalProps) => {
  const [loading, setLoading] = useState(false);
  const { refetch } = usePayoutMethods();
  
  const paypalForm = useForm({
    defaultValues: {
      paypalEmail: '',
    },
  });

  const handlePayPalOAuth = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('social-oauth-connect', {
        body: { platform: 'paypal' }
      });
      
      if (error) throw error;
      
      // Open OAuth popup
      const width = 600;
      const height = 700;
      const left = window.screen.width / 2 - width / 2;
      const top = window.screen.height / 2 - height / 2;
      
      window.open(
        data.authUrl,
        'PayPal OAuth',
        `width=${width},height=${height},left=${left},top=${top}`
      );
      
      // Listen for OAuth completion
      const handleMessage = (event: MessageEvent) => {
        if (event.data.type === 'oauth:complete' && event.data.platform === 'paypal') {
          if (event.data.status === 'success') {
            toast({ 
              title: 'Success', 
              description: 'PayPal account linked successfully!' 
            });
            refetch();
          } else {
            toast({ 
              title: 'Error', 
              description: event.data.error || 'Failed to link PayPal account',
              variant: 'destructive'
            });
          }
          window.removeEventListener('message', handleMessage);
          setLoading(false);
        }
      };
      
      window.addEventListener('message', handleMessage);
      
      // Timeout after 5 minutes
      setTimeout(() => {
        window.removeEventListener('message', handleMessage);
        setLoading(false);
      }, 300000);
      
    } catch (error: any) {
      toast({ 
        title: 'Error', 
        description: error.message || 'Failed to initiate PayPal OAuth',
        variant: 'destructive'
      });
      setLoading(false);
    }
  };

  const handlePayPalManual = async (values: { paypalEmail: string }) => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: profile } = await supabase
        .from('profiles')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (!profile) throw new Error('Profile not found');

      const { error } = await supabase
        .from('payout_methods')
        .insert({
          profile_id: profile.id,
          type: 'paypal',
          account_holder_name: user.email?.split('@')[0] || 'PayPal Account',
          metadata: {
            paypal_email: values.paypalEmail,
          },
          is_verified: false,
          is_default: false,
        });

      if (error) throw error;

      toast({ 
        title: 'Success', 
        description: 'PayPal account added successfully!' 
      });
      
      paypalForm.reset();
      refetch();
    } catch (error: any) {
      toast({ 
        title: 'Error', 
        description: error.message || 'Failed to add PayPal account',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleStripeConnect = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('social-oauth-connect', {
        body: { platform: 'stripe' }
      });
      
      if (error) throw error;
      
      const width = 600;
      const height = 700;
      const left = window.screen.width / 2 - width / 2;
      const top = window.screen.height / 2 - height / 2;
      
      window.open(
        data.authUrl,
        'Stripe Connect',
        `width=${width},height=${height},left=${left},top=${top}`
      );
      
      const handleMessage = (event: MessageEvent) => {
        if (event.data.type === 'oauth:complete' && event.data.platform === 'stripe') {
          if (event.data.status === 'success') {
            toast({ 
              title: 'Success', 
              description: 'Stripe account linked successfully!' 
            });
            refetch();
          } else {
            toast({ 
              title: 'Error', 
              description: event.data.error || 'Failed to link Stripe account',
              variant: 'destructive'
            });
          }
          window.removeEventListener('message', handleMessage);
          setLoading(false);
        }
      };
      
      window.addEventListener('message', handleMessage);
      
      setTimeout(() => {
        window.removeEventListener('message', handleMessage);
        setLoading(false);
      }, 300000);
      
    } catch (error: any) {
      toast({ 
        title: 'Error', 
        description: error.message || 'Failed to initiate Stripe Connect',
        variant: 'destructive'
      });
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Link Payment Accounts</DialogTitle>
          <DialogDescription>
            Connect your payment accounts for seamless deposits and withdrawals
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="paypal" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="paypal">PayPal</TabsTrigger>
            <TabsTrigger value="stripe">Stripe</TabsTrigger>
          </TabsList>

          <TabsContent value="paypal" className="space-y-4">
            <Alert>
              <Shield className="h-4 w-4" />
              <AlertDescription>
                <strong>OAuth (Recommended):</strong> Securely link your PayPal account with one click
              </AlertDescription>
            </Alert>
            
            <Button 
              onClick={handlePayPalOAuth} 
              className="w-full"
              disabled={loading}
            >
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 0 0-.607-.541c-.013.076-.026.175-.041.254-.93 4.778-4.005 7.201-9.138 7.201h-2.19a.563.563 0 0 0-.556.479l-1.187 7.527h-.506l-.72 4.566a.562.562 0 0 0 .554.647h3.882c.46 0 .85-.334.922-.788.06-.26.76-4.852.76-4.852a.93.93 0 0 1 .918-.788h.58c3.76 0 6.705-1.528 7.565-5.946.36-1.847.174-3.388-.777-4.471z"/>
              </svg>
              Connect with PayPal
            </Button>
            
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">Or enter manually</span>
              </div>
            </div>
            
            <Form {...paypalForm}>
              <form onSubmit={paypalForm.handleSubmit(handlePayPalManual)} className="space-y-4">
                <FormField
                  control={paypalForm.control}
                  name="paypalEmail"
                  rules={{
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address',
                    },
                  }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>PayPal Email Address</FormLabel>
                      <FormControl>
                        <Input placeholder="your-email@example.com" {...field} />
                      </FormControl>
                      <FormDescription>
                        Enter your PayPal email for withdrawals
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Add PayPal Account
                </Button>
              </form>
            </Form>
          </TabsContent>

          <TabsContent value="stripe" className="space-y-4">
            <Alert>
              <Shield className="h-4 w-4" />
              <AlertDescription>
                <strong>Stripe Connect:</strong> Link your Stripe account to receive payments directly
              </AlertDescription>
            </Alert>
            
            <Button 
              onClick={handleStripeConnect} 
              className="w-full"
              disabled={loading}
            >
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.594-7.305h.003z"/>
              </svg>
              Connect with Stripe
            </Button>
          </TabsContent>
        </Tabs>

        <LinkedAccountsList />
      </DialogContent>
    </Dialog>
  );
};
